import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Edit2, Copy, Trash2, Eye, ChevronUp, ChevronDown, X,
  BarChart3, Upload, Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";
import { youtubeId } from "@/utils/youtube";

const BACKEND_URL =
  window.APP_CONFIG?.BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  window.location.origin;
const API = `${BACKEND_URL}/api`;

const BLOCK_TYPES = [
  { type: "heading", label: "Heading" },
  { type: "paragraph", label: "Paragraph" },
  { type: "image", label: "Image" },
  { type: "comparison_table", label: "Comparison Table" },
  { type: "faq", label: "FAQ" },
  { type: "cta_button", label: "CTA Button" },
  { type: "testimonial_placeholder", label: "Testimonial (placeholder)" },
  { type: "video", label: "YouTube Video" },
  { type: "steps", label: "Numbered Steps" },
];

const emptyBlockData = (t) => {
  switch (t) {
    case "heading": return { text: "New heading", level: 2 };
    case "paragraph": return { text: "" };
    case "image": return { src: "", alt: "", caption: "" };
    case "comparison_table":
      return {
        columns: ["Option A", "Option B"],
        rows: [["Feature", "Yes", "No"]],
      };
    case "faq":
      return { items: [{ q: "", a: "" }] };
    case "cta_button": return { label: "Get my instant quote", preferred_product: "" };
    case "testimonial_placeholder": return { note: "Add a real customer review before publishing." };
    case "video": return { url: "", caption: "" };
    case "steps": return { items: [{ title: "", text: "", image: "" }] };
    default: return {};
  }
};

/**
 * Admin — /settings/advertorials.
 *
 * One page handles list AND edit. Edit mode opens inline when the admin
 * taps an article; this avoids a second route + a second file for a
 * feature that will mostly see batch edits in one sitting.
 */
export default function AdvertorialsAdmin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [kindFilter, setKindFilter] = useState("advertorial");
  const isGuide = (a) => a?.kind === "guide";
  const visibleArticles = articles.filter((a) => (kindFilter === "guide") === isGuide(a));

  useEffect(() => {
    const token = getSessionToken();
    if (!token) {
      navigate("/login");
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUser(res.data))
      .catch(() => navigate("/login"));
  }, [navigate]);

  const token = () => getSessionToken();

  const refreshList = async () => {
    const { data } = await axios.get(`${API}/advertorials`, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    setArticles(data || []);
  };

  useEffect(() => {
    if (!user) return;
    refreshList().finally(() => setLoading(false));
  }, [user]);

  const openEditor = async (articleId) => {
    try {
      const { data } = await axios.get(`${API}/advertorials/${articleId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setDraft(data);
      setEditingId(articleId);
      // Fetch analytics for this article side-by-side
      try {
        const a = await axios.get(`${API}/advertorials/${articleId}/analytics`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setAnalytics(a.data);
      } catch { /* non-fatal */ }
    } catch {
      toast.error("Could not load this article");
    }
  };

  const createNew = async () => {
    try {
      const { data } = await axios.post(
        `${API}/advertorials`,
        {
          title: kindFilter === "guide" ? "Untitled guide" : "Untitled article",
          slug: `untitled-${Math.random().toString(36).slice(2, 8)}`,
          description: "",
          status: "draft",
          kind: kindFilter,
          no_index: kindFilter === "guide",
          blocks: [],
        },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      await refreshList();
      openEditor(data.article_id);
    } catch {
      toast.error("Could not create article");
    }
  };

  const duplicateArticle = async (id) => {
    try {
      await axios.post(
        `${API}/advertorials/${id}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      await refreshList();
      toast.success("Article duplicated");
    } catch {
      toast.error("Could not duplicate");
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/advertorials/${id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      await refreshList();
      toast.success("Deleted");
      if (editingId === id) {
        setEditingId(null);
        setDraft(null);
      }
    } catch {
      toast.error("Could not delete");
    }
  };

  // --- Draft mutators (local only, saved on "Save") ----------------------
  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const updateBlock = (idx, patch) =>
    setDraft((d) => {
      const next = [...d.blocks];
      next[idx] = { ...next[idx], data: { ...next[idx].data, ...patch } };
      return { ...d, blocks: next };
    });
  const moveBlock = (idx, dir) => {
    setDraft((d) => {
      const next = [...d.blocks];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return d;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...d, blocks: next };
    });
  };
  const removeBlock = (idx) =>
    setDraft((d) => ({ ...d, blocks: d.blocks.filter((_, i) => i !== idx) }));
  const addBlock = (type) => {
    const b = {
      id: `blk_${Math.random().toString(36).slice(2, 10)}`,
      type,
      data: emptyBlockData(type),
    };
    setDraft((d) => ({ ...d, blocks: [...(d.blocks || []), b] }));
  };

  const saveDraft = async () => {
    try {
      await axios.put(
        `${API}/advertorials/${draft.article_id}`,
        {
          title: draft.title,
          slug: draft.slug,
          description: draft.description,
          hero_image: draft.hero_image,
          seo_title: draft.seo_title,
          seo_description: draft.seo_description,
          no_index: !!draft.no_index,
          status: draft.status,
          preferred_product: draft.preferred_product || null,
          kind: draft.kind || "advertorial",
          measurement_form: draft.measurement_form || { enabled: false },
          blocks: draft.blocks || [],
        },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success("Saved");
      await refreshList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const togglePublish = async () => {
    const next = draft.status === "published" ? "draft" : "published";
    try {
      await axios.put(
        `${API}/advertorials/${draft.article_id}`,
        { status: next },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      updateDraft({ status: next });
      toast.success(next === "published" ? "Published" : "Unpublished");
      await refreshList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />
      <div className="max-w-md mx-auto p-4 space-y-4">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>

        {!editingId && (
          <>
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-zinc-900">Articles & Guides</h1>
              <button
                onClick={createNew}
                data-testid="new-article-btn"
                className="h-9 px-3 rounded-lg bg-[#207024] text-white text-sm font-semibold flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                {kindFilter === "guide" ? "New Guide" : "New Article"}
              </button>
            </div>

            <KindTabs
              value={kindFilter}
              onChange={setKindFilter}
              counts={{
                advertorial: articles.filter((a) => !isGuide(a)).length,
                guide: articles.filter(isGuide).length,
              }}
            />

            <p className="text-xs text-zinc-500 italic">
              {kindFilter === "guide"
                ? "How-to pages you send to customers from Messages (+ menu → Guides), like how to measure a screen."
                : "Educational pages that funnel homeowners into the Instant Quote. Every CTA is attributed back to the article via UTM so revenue shows up in Lead Sources Analytics."}
            </p>

            {loading ? (
              <div className="text-center text-zinc-500 py-10">Loading…</div>
            ) : visibleArticles.length === 0 ? (
              <div className="text-center text-zinc-500 py-10 italic" data-testid="articles-empty">
                Nothing here yet. Tap <strong>{kindFilter === "guide" ? "New Guide" : "New Article"}</strong>.
              </div>
            ) : (
              <div className="space-y-2">
                {visibleArticles.map((a) => (
                  <div
                    key={a.article_id}
                    className="bg-white rounded-xl border border-zinc-200 p-3"
                    data-testid={`article-row-${a.article_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-zinc-900 truncate">
                            {a.title}
                          </span>
                          <span
                            className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                              a.status === "published"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-zinc-100 text-zinc-600"
                            }`}
                          >
                            {a.status}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-500 font-mono mt-0.5">
                          /learn/{a.slug}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => openEditor(a.article_id)}
                        data-testid={`edit-article-${a.article_id}`}
                        className="h-8 px-3 text-xs font-semibold rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200 flex items-center gap-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        Edit
                      </button>
                      <a
                        href={`/learn/${a.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-8 px-3 text-xs font-semibold rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200 flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        Preview
                      </a>
                      <button
                        onClick={() => duplicateArticle(a.article_id)}
                        className="h-8 px-3 text-xs font-semibold rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200 flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Duplicate
                      </button>
                      <button
                        onClick={() => deleteArticle(a.article_id)}
                        className="h-8 px-2 text-xs font-semibold rounded bg-red-50 text-red-600 hover:bg-red-100 ml-auto"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {editingId && draft && (
          <div className="space-y-3">
            <button
              onClick={() => {
                setEditingId(null);
                setDraft(null);
                setAnalytics(null);
              }}
              className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
              data-testid="exit-editor"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to list
            </button>

            {analytics && (
              <div className="bg-white rounded-xl border border-zinc-200 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900 mb-2">
                  <BarChart3 className="w-4 h-4" />
                  Last {analytics.window_days} days
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <Stat label="Views" value={analytics.views} />
                  <Stat label="CTAs" value={analytics.cta_clicks} />
                  <Stat label="Quotes" value={analytics.quotes_completed} />
                  <Stat label="Booked" value={analytics.booked_jobs} />
                  <Stat label="Converted" value={analytics.converted_jobs} />
                  <Stat label="Revenue" value={`$${(analytics.revenue || 0).toFixed(0)}`} />
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-zinc-200 p-3 space-y-3">
              <div>
                <label className="text-xs text-zinc-500">Type</label>
                <div className="mt-1 grid grid-cols-2 gap-1 p-1 bg-zinc-100 rounded-lg">
                  {[["advertorial", "Advertorial"], ["guide", "How-To Guide"]].map(([k, l]) => (
                    <button
                      key={k}
                      onClick={() => updateDraft({ kind: k })}
                      data-testid={`draft-kind-${k}`}
                      className={`h-8 rounded-md text-xs font-semibold ${
                        (draft.kind || "advertorial") === k ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Title" value={draft.title}
                onChange={(v) => updateDraft({ title: v })} testId="draft-title" />
              <Field label="Slug (URL)" value={draft.slug}
                onChange={(v) => updateDraft({ slug: v })} testId="draft-slug" prefix="/learn/" />
              <Field label="Description" value={draft.description || ""}
                onChange={(v) => updateDraft({ description: v })} testId="draft-description" textarea />
              <div>
                <label className="text-xs text-zinc-500">Hero Image</label>
                <ImageUrlInput value={draft.hero_image || ""}
                  onChange={(v) => updateDraft({ hero_image: v })} testId="draft-hero" />
              </div>
              <Field label="SEO Title (optional)" value={draft.seo_title || ""}
                onChange={(v) => updateDraft({ seo_title: v })} />
              <Field label="SEO Description (optional)" value={draft.seo_description || ""}
                onChange={(v) => updateDraft({ seo_description: v })} textarea />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!draft.no_index}
                  onChange={(e) => updateDraft({ no_index: e.target.checked })} />
                <span className="text-zinc-700">
                  Hide from search engines (<code>noindex</code>)
                </span>
              </label>
              <div>
                <label className="text-xs text-zinc-500">
                  Preferred product (default pre-select on quote)
                </label>
                <select
                  value={draft.preferred_product || ""}
                  onChange={(e) => updateDraft({ preferred_product: e.target.value || null })}
                  className="w-full h-10 rounded border border-zinc-200 px-2 text-sm"
                >
                  <option value="">— none —</option>
                  <option value="petscreen">PetScreen</option>
                  <option value="solar">Solar Screen</option>
                  <option value="sliding_door">Sliding Screen Door</option>
                  <option value="screen_door">Screen Door</option>
                </select>
              </div>
            </div>

            {isGuide(draft) && (
              <MeasurementFormSettings
                value={draft.measurement_form || {}}
                onChange={(patch) => updateDraft({ measurement_form: { ...(draft.measurement_form || {}), ...patch } })}
              />
            )}

            <div className="bg-white rounded-xl border border-zinc-200 p-3">
              <div className="font-semibold text-zinc-900 mb-2">Content Blocks</div>
              <div className="space-y-2">
                {(draft.blocks || []).map((b, i) => (
                  <BlockEditor
                    key={b.id}
                    block={b}
                    onChange={(patch) => updateBlock(i, patch)}
                    onUp={() => moveBlock(i, -1)}
                    onDown={() => moveBlock(i, 1)}
                    onRemove={() => removeBlock(i)}
                    isFirst={i === 0}
                    isLast={i === (draft.blocks || []).length - 1}
                  />
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {BLOCK_TYPES.map((t) => (
                  <button
                    key={t.type}
                    onClick={() => addBlock(t.type)}
                    className="h-8 px-2 text-xs font-semibold rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                    data-testid={`add-block-${t.type}`}
                  >
                    + {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 sticky bottom-20 bg-zinc-50 py-2">
              <button
                onClick={saveDraft}
                data-testid="save-draft-btn"
                className="flex-1 h-11 rounded-lg bg-[#207024] text-white font-semibold"
              >
                Save
              </button>
              <button
                onClick={togglePublish}
                data-testid="toggle-publish-btn"
                className={`flex-1 h-11 rounded-lg font-semibold ${
                  draft.status === "published"
                    ? "bg-amber-100 text-amber-800 border border-amber-200"
                    : "bg-emerald-600 text-white"
                }`}
              >
                {draft.status === "published" ? "Unpublish" : "Publish"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-zinc-50 rounded p-2">
      <div className="text-lg font-bold text-zinc-900">{value}</div>
      <div className="text-[10px] uppercase text-zinc-500">{label}</div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, testId, prefix }) {
  return (
    <div>
      <label className="text-xs text-zinc-500">{label}</label>
      {prefix ? (
        <div className="flex items-stretch mt-1 rounded border border-zinc-200 overflow-hidden">
          <span className="bg-zinc-50 text-zinc-500 text-xs px-2 flex items-center">
            {prefix}
          </span>
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 px-2 text-sm outline-none"
            data-testid={testId}
          />
        </div>
      ) : textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="w-full border border-zinc-200 rounded px-2 py-1 text-sm mt-1"
          data-testid={testId}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-10 border border-zinc-200 rounded px-2 text-sm mt-1"
          data-testid={testId}
        />
      )}
    </div>
  );
}

function BlockEditor({ block, onChange, onUp, onDown, onRemove, isFirst, isLast }) {
  const data = block.data || {};
  return (
    <div className="border border-zinc-200 rounded p-2 bg-zinc-50/50">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase font-semibold text-zinc-500">
          {block.type}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={onUp} disabled={isFirst} className="p-1 disabled:opacity-30">
            <ChevronUp className="w-4 h-4" />
          </button>
          <button onClick={onDown} disabled={isLast} className="p-1 disabled:opacity-30">
            <ChevronDown className="w-4 h-4" />
          </button>
          <button onClick={onRemove} className="p-1 text-red-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {block.type === "heading" && (
        <>
          <input
            value={data.text || ""}
            onChange={(e) => onChange({ text: e.target.value })}
            className="w-full h-9 border border-zinc-200 rounded px-2 text-sm"
          />
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-zinc-500">Level</span>
            {[2, 3, 4].map((l) => (
              <button
                key={l}
                onClick={() => onChange({ level: l })}
                className={`h-7 px-2 text-xs rounded ${
                  (data.level || 2) === l ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"
                }`}
              >
                H{l}
              </button>
            ))}
          </div>
        </>
      )}
      {block.type === "paragraph" && (
        <textarea
          value={data.text || ""}
          onChange={(e) => onChange({ text: e.target.value })}
          rows={3}
          className="w-full border border-zinc-200 rounded px-2 py-1 text-sm"
        />
      )}
      {block.type === "image" && (
        <div className="space-y-1">
          <ImageUrlInput value={data.src || ""} onChange={(v) => onChange({ src: v })} />
          <input
            placeholder="Alt text"
            value={data.alt || ""}
            onChange={(e) => onChange({ alt: e.target.value })}
            className="w-full h-9 border border-zinc-200 rounded px-2 text-sm"
          />
          <input
            placeholder="Caption (optional)"
            value={data.caption || ""}
            onChange={(e) => onChange({ caption: e.target.value })}
            className="w-full h-9 border border-zinc-200 rounded px-2 text-sm"
          />
        </div>
      )}
      {block.type === "comparison_table" && (
        <ComparisonTableEditor data={data} onChange={onChange} />
      )}
      {block.type === "faq" && (
        <FaqEditor data={data} onChange={onChange} />
      )}
      {block.type === "cta_button" && (
        <div className="space-y-1">
          <input
            placeholder="Button label"
            value={data.label || ""}
            onChange={(e) => onChange({ label: e.target.value })}
            className="w-full h-9 border border-zinc-200 rounded px-2 text-sm"
          />
          <select
            value={data.preferred_product || ""}
            onChange={(e) => onChange({ preferred_product: e.target.value || "" })}
            className="w-full h-9 border border-zinc-200 rounded px-2 text-sm"
          >
            <option value="">— no preselect —</option>
            <option value="petscreen">PetScreen</option>
            <option value="solar">Solar Screen</option>
            <option value="sliding_door">Sliding Screen Door</option>
            <option value="screen_door">Screen Door</option>
          </select>
        </div>
      )}
      {block.type === "video" && (
        <div className="space-y-1">
          <input
            placeholder="YouTube link (youtube.com/watch?v=… or youtu.be/…)"
            value={data.url || ""}
            onChange={(e) => onChange({ url: e.target.value })}
            className="w-full h-9 border border-zinc-200 rounded px-2 text-sm"
            data-testid="video-url-input"
          />
          {data.url && !youtubeId(data.url) && (
            <div className="text-[11px] text-red-600">That doesn't look like a YouTube link.</div>
          )}
          <input
            placeholder="Caption (optional)"
            value={data.caption || ""}
            onChange={(e) => onChange({ caption: e.target.value })}
            className="w-full h-9 border border-zinc-200 rounded px-2 text-sm"
          />
        </div>
      )}
      {block.type === "steps" && <StepsEditor data={data} onChange={onChange} />}
      {block.type === "testimonial_placeholder" && (
        <textarea
          value={data.note || ""}
          onChange={(e) => onChange({ note: e.target.value })}
          rows={2}
          placeholder="Note for the writer about what customer review to drop in."
          className="w-full border border-zinc-200 rounded px-2 py-1 text-sm"
        />
      )}
    </div>
  );
}

function ComparisonTableEditor({ data, onChange }) {
  const cols = data.columns || [];
  const rows = data.rows || [];
  const setCol = (i, v) => {
    const next = [...cols]; next[i] = v; onChange({ columns: next });
  };
  const setCell = (ri, ci, v) => {
    const next = rows.map((r) => [...r]);
    next[ri][ci] = v;
    onChange({ rows: next });
  };
  const addRow = () =>
    onChange({ rows: [...rows, Array(cols.length + 1).fill("")] });
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="w-24 text-[11px] text-zinc-500 shrink-0">Feature</span>
        {cols.map((c, i) => (
          <input
            key={i}
            value={c}
            onChange={(e) => setCol(i, e.target.value)}
            className="flex-1 h-8 border border-zinc-200 rounded px-2 text-xs"
          />
        ))}
      </div>
      {rows.map((r, ri) => (
        <div key={ri} className="flex items-center gap-1">
          {r.map((cell, ci) => (
            <input
              key={ci}
              value={cell}
              onChange={(e) => setCell(ri, ci, e.target.value)}
              className={`${ci === 0 ? "w-24 shrink-0" : "flex-1"} h-8 border border-zinc-200 rounded px-2 text-xs`}
            />
          ))}
        </div>
      ))}
      <button onClick={addRow} className="h-7 px-2 text-xs rounded bg-zinc-100 text-zinc-700">
        + row
      </button>
    </div>
  );
}

function FaqEditor({ data, onChange }) {
  const items = data.items || [];
  const setItem = (i, patch) => {
    const next = items.map((it, j) => (j === i ? { ...it, ...patch } : it));
    onChange({ items: next });
  };
  const addItem = () =>
    onChange({ items: [...items, { q: "", a: "" }] });
  const removeItem = (i) =>
    onChange({ items: items.filter((_, j) => j !== i) });
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="border border-zinc-200 rounded p-2 bg-white">
          <input
            placeholder="Question"
            value={it.q}
            onChange={(e) => setItem(i, { q: e.target.value })}
            className="w-full h-8 border border-zinc-200 rounded px-2 text-xs mb-1"
          />
          <textarea
            placeholder="Answer"
            value={it.a}
            onChange={(e) => setItem(i, { a: e.target.value })}
            rows={2}
            className="w-full border border-zinc-200 rounded px-2 py-1 text-xs"
          />
          <button
            onClick={() => removeItem(i)}
            className="text-[11px] text-red-500 mt-1"
          >
            Remove Q
          </button>
        </div>
      ))}
      <button onClick={addItem} className="h-7 px-2 text-xs rounded bg-zinc-100 text-zinc-700">
        + question
      </button>
    </div>
  );
}

function KindTabs({ value, onChange, counts }) {
  return (
    <div className="grid grid-cols-2 gap-1 p-1 bg-zinc-200/60 rounded-lg" data-testid="kind-tabs">
      {[["advertorial", "Advertorials"], ["guide", "How-To Guides"]].map(([k, l]) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          data-testid={`kind-tab-${k}`}
          className={`h-9 rounded-md text-sm font-semibold transition-colors ${
            value === k ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"
          }`}
        >
          {l} <span className="text-xs text-zinc-400">({counts[k] || 0})</span>
        </button>
      ))}
    </div>
  );
}

function ImageUrlInput({ value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await axios.post(`${API}/messages/upload-image`, fd, {
        headers: { Authorization: `Bearer ${getSessionToken()}` },
      });
      onChange(data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex gap-1 mt-1">
      <input
        placeholder="Image URL"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 h-9 border border-zinc-200 rounded px-2 text-sm"
        data-testid={testId}
      />
      <label className="h-9 px-2 rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs font-semibold flex items-center gap-1 cursor-pointer shrink-0">
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        Upload
        <input type="file" accept="image/*" className="hidden" disabled={uploading}
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
    </div>
  );
}

function StepsEditor({ data, onChange }) {
  const items = data.items || [];
  const setItem = (i, patch) => onChange({ items: items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ items: next });
  };
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="border border-zinc-200 rounded p-2 bg-white space-y-1" data-testid={`step-editor-${i}`}>
          <div className="flex items-center gap-1">
            <span className="w-6 h-6 rounded-full bg-[#207024] text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
            <input placeholder="Step title" value={it.title || ""}
              onChange={(e) => setItem(i, { title: e.target.value })}
              className="flex-1 min-w-0 h-8 border border-zinc-200 rounded px-2 text-xs" />
            <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
            <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
            <button onClick={() => onChange({ items: items.filter((_, j) => j !== i) })} className="p-1 text-red-500"><X className="w-3.5 h-3.5" /></button>
          </div>
          <textarea placeholder="Instructions" value={it.text || ""} rows={2}
            onChange={(e) => setItem(i, { text: e.target.value })}
            className="w-full border border-zinc-200 rounded px-2 py-1 text-xs" />
          <ImageUrlInput value={it.image || ""} onChange={(v) => setItem(i, { image: v })} />
        </div>
      ))}
      <button onClick={() => onChange({ items: [...items, { title: "", text: "", image: "" }] })}
        className="h-7 px-2 text-xs rounded bg-zinc-100 text-zinc-700" data-testid="add-step-btn">
        + step
      </button>
    </div>
  );
}

function MeasurementFormSettings({ value, onChange }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-3 space-y-3" data-testid="measurement-form-settings">
      <label className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
        <input type="checkbox" checked={!!value.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })} data-testid="mf-enabled" />
        "Submit my measurements" form
      </label>
      <p className="text-[11px] text-zinc-500 -mt-2">
        Customers enter width × height × qty (plus an optional photo). It lands in their Messages thread and their Saved Measurements.
      </p>
      {value.enabled && (
        <div className="border-t border-zinc-100 pt-3 space-y-2">
          <label className="flex items-center gap-2 text-sm text-zinc-800">
            <input type="checkbox" checked={!!value.upsell_enabled}
              onChange={(e) => onChange({ upsell_enabled: e.target.checked })} data-testid="mf-upsell-enabled" />
            Upsell offer: "Add more screens and save"
          </label>
          {value.upsell_enabled && (
            <div className="grid grid-cols-2 gap-2">
              <NumberField label="Percent off (whole order)" suffix="%" value={value.upsell_percent ?? 10}
                onChange={(v) => onChange({ upsell_percent: v })} testId="mf-upsell-percent" />
              <NumberField label="Minimum screens" value={value.upsell_min_screens ?? 2}
                onChange={(v) => onChange({ upsell_min_screens: v })} testId="mf-upsell-min" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NumberField({ label, value, onChange, suffix, testId }) {
  return (
    <div>
      <label className="text-xs text-zinc-500">{label}</label>
      <div className="flex items-center mt-1 border border-zinc-200 rounded overflow-hidden">
        <input type="number" min="0" inputMode="numeric" value={value}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="flex-1 min-w-0 h-9 px-2 text-sm outline-none" data-testid={testId} />
        {suffix && <span className="px-2 text-xs text-zinc-500 bg-zinc-50 h-9 flex items-center">{suffix}</span>}
      </div>
    </div>
  );
}
